import FileIcons from '../assets/file-icons';
import {
    isTrainingVideoFile,
    resolveTrainingFileType,
    TrainingAudience,
    TrainingCategory,
    TrainingMaterial,
    TRAINING_MAX_IMAGE_PDF_BYTES,
    TRAINING_MAX_VIDEO_BYTES,
} from '../types/training';

const EXTENSION_ALIASES: Record<string, keyof typeof FileIcons> = {
    JPEG: 'JPG',
    JPG: 'JPG',
    WEBP: 'PNG',
    WEBM: 'MP4',
};

export function normalizeTrainingAudiences(
    audiences?: TrainingAudience[] | null,
): TrainingAudience[] {
    if (!audiences?.length) {
        return ['GENERAL'];
    }
    return audiences;
}

export function formatTrainingFileSize(bytes: number): string {
    if (bytes <= 0) return '-';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) {
        return `${mb.toFixed(1)} MB`;
    }
    return `${(bytes / 1024).toFixed(1)} KB`;
}

export function getTrainingFileIconSrc(fileName: string): string {
    const extension = fileName.split('.').pop()?.toUpperCase() || 'UNK';
    const mapped =
        EXTENSION_ALIASES[extension] ?? (extension as keyof typeof FileIcons);
    return FileIcons[mapped] ?? FileIcons.UNK;
}

export function toTrainingCategorySelectOptions(
    categories: TrainingCategory[],
): { value: string; label: string }[] {
    return categories.map(category => ({
        value: String(category.id),
        label: category.name,
    }));
}

export function getTrainingFileAcceptAttr(): string {
    return 'image/jpeg,image/png,image/gif,image/webp,application/pdf,video/mp4,video/webm,.mp4,.webm,.pdf';
}

export function getTrainingAcceptedTypesLabel(): string {
    return 'JPEG, PNG, GIF, WebP, PDF, MP4, WebM';
}

export function getMaxSizeForTrainingFile(
    file: File,
    mimeType: string,
): number {
    return isTrainingVideoFile(file, mimeType)
        ? TRAINING_MAX_VIDEO_BYTES
        : TRAINING_MAX_IMAGE_PDF_BYTES;
}

export type TrainingFileValidationResult =
    | { ok: true; mimeType: string }
    | { ok: false; error: string };

export function validateTrainingFile(file: File): TrainingFileValidationResult {
    const mimeType = resolveTrainingFileType(file);
    if (!mimeType) {
        return {
            ok: false,
            error: `Supported formats: ${getTrainingAcceptedTypesLabel()}`,
        };
    }

    if (file.size > getMaxSizeForTrainingFile(file, mimeType)) {
        return {
            ok: false,
            error: isTrainingVideoFile(file, mimeType)
                ? 'Video must be 500 MB or smaller'
                : 'File must be 50 MB or smaller',
        };
    }

    return { ok: true, mimeType };
}

export function filterTrainingMaterialsBySearch(
    materials: TrainingMaterial[],
    search: string,
): TrainingMaterial[] {
    const normalizedSearch = search.trim().toLowerCase();
    if (normalizedSearch.length === 0) {
        return materials;
    }

    return materials.filter(material => {
        const haystack = [
            material.title,
            material.description ?? '',
            material.fileName,
        ]
            .join(' ')
            .toLowerCase();
        return haystack.includes(normalizedSearch);
    });
}
